import type { Vehicle, VehicleType } from '@fc-core';
import {
  getCurrentDriver,
  getDriverFullName,
} from '@fc-core/models/vehicle/vehicle-driver.utils';
import type { FilterItem } from '@fc-vehicles/components/shared/vehicle-filter/vehicles-filter.component';
import type { SortBy } from '@fc-shared/ui/sort-by/sort-by';
import { sortByPropertyWithOrder } from '@fc-shared/utils/sort-by-property';
import moment from 'moment';
import type { SearchBy } from '../reducers/vehicle-filters.reducer';

export const applySelectedVehicleFilters = (
  vehicles: Vehicle[],
  vehicleFilters: FilterItem[],
): Vehicle[] => {
  if (!vehicleFilters?.length) return vehicles;

  const statusNames = vehicleFilters
    .filter((filter) => filter.type === 'status')
    .map((filter) => filter.name.toLowerCase());
  const typeIds = vehicleFilters
    .filter((filter) => filter.type === 'type')
    .map((filter) => filter.id);
  const qrFilters = vehicleFilters.filter((filter) => filter.type === 'qr');

  let filteredVehicles = vehicles;
  if (statusNames.length) {
    filteredVehicles = filteredVehicles.filter(
      (vehicle) =>
        !!vehicle.lastState?.type &&
        statusNames.includes(vehicle.lastState.type),
    );
  }
  if (typeIds.length) {
    filteredVehicles = filteredVehicles.filter((vehicle) =>
      typeIds.includes(vehicle.type),
    );
  }
  // Selecting both "linked" and "not linked" means no QR filtering at all.
  if (qrFilters.length === 1) {
    const showLinked = qrFilters[0].id === 'linked';
    filteredVehicles = filteredVehicles.filter((vehicle) =>
      showLinked ? vehicle.hasQrCode : !vehicle.hasQrCode,
    );
  }
  return filteredVehicles;
};

export const vehicleSearchFilter = (
  vehicles: Vehicle[],
  searchText: string,
  vehicleTypes: VehicleType[],
  searchFields: SearchBy[],
): Vehicle[] => {
  const search = searchText?.trim().toLowerCase();
  if (!search) return vehicles;

  return vehicles.filter(
    (vehicle) =>
      (searchFields?.includes('identifier') &&
        matchesIdentifier(vehicle, search)) ||
      (searchFields?.includes('vehicleInfo') &&
        matchesVehicleInfo(vehicle, search)) ||
      (searchFields?.includes('type') &&
        matchesTypeName(vehicleTypes, vehicle, search)) ||
      (searchFields?.includes('operators') &&
        matchesCurrentDriver(vehicle, search)),
  );
};

const matchesIdentifier = (vehicle: Vehicle, search: string): boolean =>
  vehicle.identifier.toLowerCase().includes(search);

const matchesVehicleInfo = (vehicle: Vehicle, search: string): boolean =>
  `${vehicle.manufacturer?.name || ''} ${vehicle.model || ''}`
    .toLowerCase()
    .includes(search);

const matchesTypeName = (
  vehicleTypes: VehicleType[],
  vehicle: Vehicle,
  search: string,
): boolean => {
  const vehicleType = vehicleTypes.find((type) => type.id === vehicle.type);
  return !!vehicleType && vehicleType.name.toLowerCase() === search;
};

const matchesCurrentDriver = (vehicle: Vehicle, search: string): boolean => {
  const driver = getCurrentDriver(vehicle);
  return !!driver && getDriverFullName(driver).toLowerCase().includes(search);
};

export const sortVehicles = (
  vehicles: Vehicle[],
  sortBy: SortBy | null | undefined,
): Vehicle[] => {
  if (!vehicles?.length) return [];
  if (!sortBy) return vehicles;
  const order = sortBy.order ?? false;

  switch (sortBy.value) {
    case 'identifier':
      return sortByPropertyWithOrder<Vehicle>(vehicles, 'identifier', order);
    case 'manufacturer':
      return sortWithEmptyValuesLast(
        vehicles,
        (vehicle) => vehicle.manufacturer?.name,
        (aName, bName) =>
          order ? bName.localeCompare(aName) : aName.localeCompare(bName),
      );
    case 'model':
      return sortByPropertyWithOrder<Vehicle>(vehicles, 'model', order);
    case 'created':
      return sortWithEmptyValuesLast(
        vehicles,
        (vehicle) => vehicle.created,
        (aCreated, bCreated) =>
          order
            ? moment(aCreated).diff(moment(bCreated))
            : moment(bCreated).diff(moment(aCreated)),
      );
    case 'last_updated':
      return [
        ...vehicles.filter((vehicle) => !vehicle.lastReading?.datetime),
        ...vehicles
          .filter((vehicle) => !!vehicle.lastReading?.datetime)
          .sort((a, b) => {
            const aReadingTime = a.lastReading?.datetime;
            const bReadingTime = b.lastReading?.datetime;
            return order
              ? moment(bReadingTime).diff(aReadingTime)
              : moment(aReadingTime).diff(bReadingTime);
          }),
      ];
    default:
      return vehicles;
  }
};

const sortWithEmptyValuesLast = <Value>(
  vehicles: Vehicle[],
  getValue: (vehicle: Vehicle) => Value | null | undefined,
  compareValues: (a: Value, b: Value) => number,
): Vehicle[] =>
  [...vehicles].sort((aVehicle, bVehicle) => {
    const aValue = getValue(aVehicle);
    const bValue = getValue(bVehicle);
    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;
    return compareValues(aValue, bValue);
  });
